import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

interface IdleTimeoutHandlerProps {
  onLogout: () => void;
  accessTokenTimeout?: number; // in minutes
  idleTimeout?: number; // in minutes
  alertModalTime?: number; // in minutes
}

const IdleTimeoutHandler: React.FC<IdleTimeoutHandlerProps> = ({ 
  onLogout,
  accessTokenTimeout = 2,
  idleTimeout = 2, 
  alertModalTime = 0.5
}) => {

  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  // Convert all times to milliseconds
  const accessTokenTimeoutMs = accessTokenTimeout * 60 * 1000;
  const idleTimeoutMs = idleTimeout * 60 * 1000;
  const alertModalTimeMs = alertModalTime * 60 * 1000;

  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
    setOpen(false);
    // Update last activity in localStorage for cross-tab sync
    localStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  // Handle cross-tab activity
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lastActivity') {
        setLastActivity(Number(e.newValue));
        setOpen(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleUserActivity = () => {
      resetTimer();
    };

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, [resetTimer]);

  useEffect(() => {
    const checkIdle = () => {
      const now = Date.now();
      const timeElapsed = now - lastActivity;
      const timeUntilIdleTimeout = idleTimeoutMs - timeElapsed;
      const timeUntilAccessTokenTimeout = accessTokenTimeoutMs - timeElapsed;

      // Check for access token timeout first
      if (timeUntilAccessTokenTimeout <= 0) {
        onLogout();
        return;
      }

      // Then check for idle timeout
      if (timeUntilIdleTimeout <= 0) {
        onLogout();
      } else if (timeUntilIdleTimeout <= alertModalTimeMs) {
        setOpen(true);
        setRemaining(Math.ceil(timeUntilIdleTimeout / 1000));
      }
    };

    const interval = setInterval(checkIdle, 1000);
    return () => clearInterval(interval);
  }, [lastActivity, idleTimeoutMs, accessTokenTimeoutMs, alertModalTimeMs, onLogout]);

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Session Expiring Soon</DialogTitle>
      <DialogContent>
        <Typography>
          You have been inactive for a while. Your session will expire in{' '}
          <strong>{remaining}</strong> seconds.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleStayLoggedIn} variant="contained" color="primary">
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutHandler; 